import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveChannel } from '@/store/activeChannel';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type DreLine =
  | 'revenue_gross' | 'deduction_taxes' | 'deduction_returns' | 'deduction_commissions'
  | 'cogs'
  | 'opex_sales' | 'opex_admin' | 'opex_other'
  | 'depreciation'
  | 'financial_revenue' | 'financial_expense'
  | 'taxes_on_profit';

export interface DreLineConfig {
  label:    string;
  sign:     1 | -1;   // 1 = soma, -1 = subtrai do resultado
  section:  string;
  indent:   boolean;
}

export const DRE_CONFIG: Record<DreLine, DreLineConfig> = {
  revenue_gross:         { label: 'Receita Bruta',             sign:  1, section: 'receita',     indent: false },
  deduction_taxes:       { label: '(−) Impostos s/ vendas',    sign: -1, section: 'deducoes',    indent: true  },
  deduction_returns:     { label: '(−) Devoluções',            sign: -1, section: 'deducoes',    indent: true  },
  deduction_commissions: { label: '(−) Comissões/taxas',       sign: -1, section: 'deducoes',    indent: true  },
  cogs:                  { label: '(−) CMV / CPV',             sign: -1, section: 'cogs',        indent: false },
  opex_sales:            { label: 'Despesas com vendas',       sign: -1, section: 'opex',        indent: true  },
  opex_admin:            { label: 'Despesas administrativas',  sign: -1, section: 'opex',        indent: true  },
  opex_other:            { label: 'Outras despesas operac.',   sign: -1, section: 'opex',        indent: true  },
  depreciation:          { label: '(−) Depreciação/Amort.',    sign: -1, section: 'depreciacao', indent: false },
  financial_revenue:     { label: '(+) Receitas financeiras',  sign:  1, section: 'financeiro',  indent: true  },
  financial_expense:     { label: '(−) Despesas financeiras',  sign: -1, section: 'financeiro',  indent: true  },
  taxes_on_profit:       { label: '(−) IR / CSLL',             sign: -1, section: 'impostos',    indent: false },
};

export interface DreRow {
  line:         DreLine;
  label:        string;
  indent:       boolean;
  amount_cents: number;         // valor absoluto do período atual
  av_pct:       number | null;  // % sobre receita líquida (análise vertical)
  ah_pct:       number | null;  // variação vs período anterior (análise horizontal)
  prev_cents:   number;
}

export interface DreResult {
  rows:             DreRow[];
  revenue_net:      number;
  gross_profit:     number;
  ebitda:           number;
  net_income:       number;
  gross_margin_pct: number;
  ebitda_margin_pct: number;
  net_margin_pct:   number;
  prev_net_income:  number;
}

// ─── Helpers de data ──────────────────────────────────────────────────────────

export type Period = 'mtd' | 'qtd' | 'ytd' | 'custom';

export function periodDates(period: Period, customFrom?: string, customTo?: string): { from: string; to: string } {
  const today = new Date();
  const fmt   = (d: Date) => d.toISOString().slice(0, 10);
  const to    = fmt(today);

  if (period === 'custom') return { from: customFrom ?? to, to: customTo ?? to };

  const from = new Date(today);
  if (period === 'mtd') { from.setDate(1); }
  else if (period === 'qtd') { from.setMonth(Math.floor(from.getMonth() / 3) * 3, 1); }
  else if (period === 'ytd') { from.setMonth(0, 1); }

  return { from: fmt(from), to };
}

function prevPeriod(from: string, to: string): { from: string; to: string } {
  const f = new Date(from);
  const t = new Date(to);
  const days = Math.round((t.getTime() - f.getTime()) / 86400000) + 1;
  t.setDate(f.getDate() - 1);
  f.setDate(f.getDate() - days);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(f), to: fmt(t) };
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useDREDetailed(from: string, to: string) {
  const { activeChannelId } = useActiveChannel();
  const [result, setResult] = useState<DreResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    setError('');

    const prev = prevPeriod(from, to);

    Promise.all([
      supabase.rpc('dre_monthly' as never, { p_from: from, p_to: to, p_channel: activeChannelId ?? null } as never),
      supabase.rpc('dre_monthly' as never, { p_from: prev.from, p_to: prev.to, p_channel: activeChannelId ?? null } as never),
    ]).then(([curr, prevR]: any[]) => {
      if (curr.error) { setError(curr.error.message); setLoading(false); return; }
      if (prevR.error) { setError(prevR.error.message); setLoading(false); return; }

      const currMap = new Map<DreLine, number>();
      const prevMap = new Map<DreLine, number>();

      for (const row of (curr.data ?? [])) {
        const line = row.dre_line as string;
        if (line in DRE_CONFIG) currMap.set(line as DreLine, Number(row.amount_cents) || 0);
      }
      for (const row of (prevR.data ?? [])) {
        const line = row.dre_line as string;
        if (line in DRE_CONFIG) prevMap.set(line as DreLine, Number(row.amount_cents) || 0);
      }

      const get  = (m: Map<DreLine, number>, k: DreLine) => m.get(k) ?? 0;
      const curr_gross   = get(currMap, 'revenue_gross');
      const curr_deductions = [
        get(currMap, 'deduction_taxes'),
        get(currMap, 'deduction_returns'),
        get(currMap, 'deduction_commissions'),
      ].reduce((s, v) => s + v, 0);
      const revenue_net  = curr_gross - curr_deductions;
      const gross_profit = revenue_net - get(currMap, 'cogs');
      const opex         = get(currMap, 'opex_sales') + get(currMap, 'opex_admin') + get(currMap, 'opex_other');
      const ebitda       = gross_profit - opex;
      const ebit         = ebitda - get(currMap, 'depreciation');
      const fin          = get(currMap, 'financial_revenue') - get(currMap, 'financial_expense');
      const lair         = ebit + fin;
      const net_income   = lair - get(currMap, 'taxes_on_profit');

      const prev_gross   = get(prevMap, 'revenue_gross');
      const prev_deductions = [
        get(prevMap, 'deduction_taxes'),
        get(prevMap, 'deduction_returns'),
        get(prevMap, 'deduction_commissions'),
      ].reduce((s, v) => s + v, 0);
      const prev_net_income = prev_gross - prev_deductions - get(prevMap, 'cogs')
        - get(prevMap, 'opex_sales') - get(prevMap, 'opex_admin') - get(prevMap, 'opex_other')
        - get(prevMap, 'depreciation') + get(prevMap, 'financial_revenue')
        - get(prevMap, 'financial_expense') - get(prevMap, 'taxes_on_profit');

      const av = (v: number) => revenue_net !== 0 ? (v / revenue_net) * 100 : null;
      const ah = (curr: number, prev: number) => prev !== 0 ? ((curr - prev) / Math.abs(prev)) * 100 : null;

      const rows: DreRow[] = (Object.keys(DRE_CONFIG) as DreLine[]).map((line) => {
        const cfg   = DRE_CONFIG[line];
        const cents = get(currMap, line);
        const prev  = get(prevMap, line);
        return {
          line,
          label:        cfg.label,
          indent:       cfg.indent,
          amount_cents: cents,
          av_pct:       av(cents),
          ah_pct:       ah(cents, prev),
          prev_cents:   prev,
        };
      }).filter((r) => r.amount_cents !== 0 || r.prev_cents !== 0);

      setResult({
        rows,
        revenue_net,
        gross_profit,
        ebitda,
        net_income,
        gross_margin_pct:  revenue_net !== 0 ? (gross_profit / revenue_net) * 100 : 0,
        ebitda_margin_pct: revenue_net !== 0 ? (ebitda / revenue_net) * 100 : 0,
        net_margin_pct:    revenue_net !== 0 ? (net_income / revenue_net) * 100 : 0,
        prev_net_income,
      });
      setLoading(false);
    }).catch((e: Error) => {
      setError(e.message ?? 'Erro ao carregar DRE.');
      setLoading(false);
    });
  }, [from, to, activeChannelId]);

  return { result, loading, error };
}
